import type { ReactElement } from 'react'
import DocViewer from '@cyntler/react-doc-viewer'
import MSDocRenderer from '@cyntler/react-doc-viewer/dist/esm/renderers/msdoc'
import CSVDocRenderer from '@cyntler/react-doc-viewer/dist/esm/renderers/csv'
import MuiBox from '@mui/material/Box'
import type { Attachment } from '../../../../../types/Document'
import type { PdfParams } from '../../../../../hooks/usePdf'
import { FILE_TYPES, type FileType, isImageType, isExcelType, isDocxType, isDocType } from '../fileType'
import { FlexRow } from '../../../../atoms/layout/Flex'
import ImageViewer from '../../../../atoms/display/ImageViewer'
import RelativeBox from '../../../../atoms/layout/RelativeBox'
import Label from '../../../../atoms/display/Label'
import PdfViewer from '../../../../atoms/display/PdfViewer'
import DocxViewer from '../DocxViewer'
import { registerBinaryOctetStreamSupport } from '../docViewerConfig'

// binary/octet-stream MIMEタイプのファイルをMS Office/CSVドキュメントとして認識させる
// サーバーが適切なContent-Typeを返さない場合でもプレビューを可能にする
registerBinaryOctetStreamSupport()

/** 添付ファイルコンテンツ表示のプロパティ */
export interface AttachmentContentProps {
  readonly attachment: Attachment
  readonly fileType: FileType
  readonly height: number | string
  readonly pdfParams: PdfParams
  /** PC版かどうか（レイアウト調整用） */
  readonly isPC: boolean
}

/** Officeドキュメントビューアの幅（PC版） */
const OFFICE_VIEWER_WIDTH_PC = 640

/** レンダリング不可時の代替表示 */
function NoRendererView(): ReactElement {
  return <Label text="プレビューできないファイルです" />
}

/**
 * 添付ファイルのコンテンツを表示するコンポーネント
 * PC/SP共通で使用され、ファイルタイプに応じた表示を行う
 */
export default function AttachmentContent({
  attachment,
  fileType,
  height,
  pdfParams,
  isPC,
}: AttachmentContentProps): ReactElement {
  // 画像
  if (isImageType(fileType)) {
    if (isPC) {
      return (
        <RelativeBox>
          <FlexRow align="center" justify="center" height={height}>
            <ImageViewer src={attachment.url} alt="image" />
          </FlexRow>
        </RelativeBox>
      )
    }
    return (
      <MuiBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <ImageViewer src={attachment.url} alt="image" />
      </MuiBox>
    )
  }

  // DOCX（mammoth.jsでクライアントサイドレンダリング）
  if (isDocxType(fileType)) {
    return <DocxViewer url={attachment.url} height={height} isPC={isPC} />
  }

  // DOC（レガシー形式はクライアントサイドでレンダリングできないためプレビュー不可）
  if (isDocType(fileType)) {
    return <Label text="プレビューできないファイルです" />
  }

  // Excel（Microsoft Office Online経由）
  if (isExcelType(fileType)) {
    return (
      <DocViewer
        documents={[{ uri: attachment.url }]}
        pluginRenderers={[MSDocRenderer]}
        prefetchMethod="GET"
        config={{ noRenderer: { overrideComponent: NoRendererView } }}
        style={isPC ? { width: OFFICE_VIEWER_WIDTH_PC, height } : { width: '100%', height: '100%' }}
      />
    )
  }

  // CSV
  if (fileType === FILE_TYPES.csv) {
    return (
      <DocViewer
        documents={[{ uri: attachment.url }]}
        pluginRenderers={[CSVDocRenderer]}
        prefetchMethod="GET"
        config={{ noRenderer: { overrideComponent: NoRendererView } }}
        style={isPC ? { height } : { width: '100%', height: '100%' }}
      />
    )
  }

  // PDF
  if (fileType === FILE_TYPES.pdf) {
    // PC版: pageWidth（横幅に合わせて表示）
    // 縦長PDFの場合はスクロールが必要になり、より大きく表示される
    if (isPC) {
      return (
        <PdfViewer
          url={attachment.url}
          httpHeaders={{}}
          height={height}
          defaultZoomLevel="pageWidth"
          params={pdfParams}
        />
      )
    }
    // SP版: pageWidth（横幅いっぱいに表示）
    // スマートフォンでは画面幅が狭いため、PDFを横幅に合わせて表示することで
    // 文字が読みやすくなり、ユーザーは縦スクロールのみでPDFを閲覧できる
    return (
      <MuiBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <PdfViewer
          url={attachment.url}
          httpHeaders={{}}
          width="100%"
          height="100%"
          defaultZoomLevel="pageWidth"
          params={pdfParams}
        />
      </MuiBox>
    )
  }

  // Unknown
  return <Label text="プレビューできないファイルです" />
}
